import React, { useEffect, useMemo, useRef, useState } from "react";
import { Layer, Line, Stage } from "react-konva";
import { updateStepClustersIncremental } from "../lib/dynamicInkSegmenter";

const MIN_STAGE_WIDTH = 320;
const MIN_STAGE_HEIGHT = 420;
const DEFAULT_LANE_HEIGHT = 90;
const STAGE_CONTENT_RIGHT_PAD = 36;
const SLATE_RULE_STROKE = "#e2e8f0";
const SLATE_RULE_STROKE_WIDTH = 1.0;

function computeBounds(lines) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  (lines || []).forEach((line) => {
    const points = Array.isArray(line?.points) ? line.points : [];
    for (let i = 0; i < points.length; i += 2) {
      const x = Number(points[i]);
      const y = Number(points[i + 1]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  });

  if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
    return null;
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY)
  };
}

function getStatusMeta(status) {
  if (status === "incorrect_but_right_direction") return { label: "Fix", className: "status-incomplete" };
  if (status === "wrong_direction") return { label: "Off path", className: "status-incorrect" };
  if (status === "dependent_on_error") return { label: "Blocked", className: "status-review" };
  if (status === "ahead_of_frontier") return { label: "Later", className: "status-unclear" };
  if (status === "superseded") return { label: "Replaced", className: "status-review" };
  if (status === "incorrect") return { label: "Incorrect", className: "status-incorrect" };
  if (status === "incomplete") return { label: "Incomplete", className: "status-incomplete" };
  if (status === "missing_step") return { label: "Missing", className: "status-missing" };
  if (status === "unclear") return { label: "Unclear", className: "status-unclear" };
  if (status === "correct") return { label: "Correct", className: "status-correct" };
  return { label: "Review", className: "status-review" };
}

export default function SlatePreviewRender({
  lines = [],
  stageWidth = null,
  stageHeight = null,
  laneHeights = [],
  stepClusters = [],
  stepDiagnostics = {},
  selectedStep = null,
  onSelectStep = null
}) {
  const hostRef = useRef(null);
  const [hostWidth, setHostWidth] = useState(0);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const nextWidth = Math.max(0, Math.floor(entry?.contentRect?.width || 0));
      setHostWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    });

    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  const preview = useMemo(() => {
    const bounds = computeBounds(lines);
    if (!bounds) return null;

    const savedWidth = Math.ceil(Number(stageWidth) || 0);
    const responsiveWidth = Math.max(0, Math.floor(hostWidth) - 24);
    const width = Math.max(
      MIN_STAGE_WIDTH,
      savedWidth,
      responsiveWidth,
      Math.ceil(bounds.maxX + STAGE_CONTENT_RIGHT_PAD)
    );
    const normalizedLaneHeights = Array.isArray(laneHeights)
      ? laneHeights.map((value) => Math.max(24, Number(value) || DEFAULT_LANE_HEIGHT)).filter(Boolean)
      : [];
    const inferredLaneCount = Math.max(6, Math.ceil((bounds.maxY + DEFAULT_LANE_HEIGHT) / DEFAULT_LANE_HEIGHT));
    const rules = normalizedLaneHeights.length
      ? [0, ...normalizedLaneHeights.reduce((acc, laneHeight) => {
          const last = acc.length ? acc[acc.length - 1] : 0;
          acc.push(last + laneHeight);
          return acc;
        }, [])]
      : Array.from({ length: inferredLaneCount + 1 }, (_, index) => index * DEFAULT_LANE_HEIGHT);
    const inferredHeight = rules.length ? rules[rules.length - 1] : inferredLaneCount * DEFAULT_LANE_HEIGHT;
    const height = Math.max(MIN_STAGE_HEIGHT, Math.ceil(Number(stageHeight) || 0), Math.ceil(inferredHeight));

    return { width, height, rules };
  }, [hostWidth, laneHeights, lines, stageHeight, stageWidth]);

  const clusters = useMemo(() => {
    if (!Array.isArray(lines) || lines.length === 0) return [];
    if (Array.isArray(stepClusters) && stepClusters.length > 0) return stepClusters;
    try {
      return updateStepClustersIncremental([], lines, { canvasWidth: preview?.width || 800 });
    } catch (err) {
      return [];
    }
  }, [lines, stepClusters, preview?.width]);

  if (!preview) {
    return <div className="question-meta">No slate image saved.</div>;
  }

  return (
    <div ref={hostRef} className="slate-preview-shell" style={{ overflowX: "auto", overflowY: "auto" }}>
      <div className="slate-shell slate-mode profile-desktop slate-preview-mode" style={{ width: "max-content", minWidth: "100%", overflow: "visible" }}>
        <div className="slate-stage scroll-mode slate-preview-stage" style={{ overflow: "visible", width: "max-content", minWidth: "100%" }}>
          <div className="slate-stage-content" style={{ position: "relative", width: `${preview.width}px`, minWidth: `${preview.width}px` }}>
            <div
              className="slate-canvas-pane scroll-mode"
              style={{ position: "relative", width: `${preview.width}px`, minWidth: `${preview.width}px` }}
            >
              {/* ── Step Rail on Left ── */}
              {clusters.length > 0 && (
                <div className="slate-left-step-rail" aria-label="Step Rail">
                  {clusters.map((cluster) => {
                    const b = cluster.bounds;
                    if (!b || b.width < 6) return null;
                    const top = Math.round((b.minY + b.maxY) / 2);
                    const stepNo = Number(cluster.stepNo);
                    const diag = stepDiagnostics?.[stepNo] || stepDiagnostics?.[String(stepNo)] || null;
                    const status = String(diag?.status || "");
                    const isIncorrect = status === "incorrect" || status === "wrong_direction";
                    const isIncomplete = status === "incomplete" || status === "incorrect_but_right_direction";
                    const isCorrect = status === "correct";
                    const hasDiag = Boolean(diag);
                    const statusMeta = hasDiag ? getStatusMeta(status) : null;
                    const detailText = diag?.reason || statusMeta?.label || `Step ${stepNo}`;
                    const isSelected = Number(selectedStep) === stepNo;

                    return (
                      <div
                        key={`step_rail_${cluster.id || stepNo}`}
                        className="slate-step-rail-item"
                        style={{ top: `${top}px` }}
                      >
                        <button
                          type="button"
                          className={`slate-step-marker ${
                            hasDiag
                              ? `${statusMeta?.className || ""} ${isIncorrect ? "shape-triangle" : isIncomplete ? "shape-rhombus" : "shape-circle"}`
                              : "default-step-badge"
                          } ${isSelected ? "is-selected" : ""}`}
                          title={detailText}
                          aria-label={`Step ${stepNo}: ${detailText}`}
                          onClick={() => {
                            if (typeof onSelectStep === "function") {
                              onSelectStep(isSelected ? null : stepNo);
                            }
                          }}
                        >
                          <span className="slate-step-marker-number">{stepNo}</span>
                          {hasDiag && (
                            <span
                              className={`slate-step-popover tutor-note ${statusMeta?.className || ""} ${
                                isSelected ? "is-visible" : ""
                              }`}
                            >
                              <strong className="slate-step-popover-title">
                                Step {stepNo} - {statusMeta?.label || status}
                              </strong>
                              <span>{detailText}</span>
                            </span>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <Stage width={preview.width} height={preview.height}>
                <Layer>
                  {/* Clean, faint solid ruled lines (No legacy dotted lines) */}
                  {preview.rules.map((y) => (
                    <Line
                      key={`review-rule-${y}`}
                      points={[0, y, preview.width, y]}
                      stroke={SLATE_RULE_STROKE}
                      strokeWidth={SLATE_RULE_STROKE_WIDTH}
                      lineCap="round"
                      listening={false}
                    />
                  ))}
                  {lines.map((line, index) => {
                    const points = Array.isArray(line?.points) ? line.points : [];
                    return (
                      <Line
                        key={line.id || `review-line-${index}`}
                        points={points}
                        stroke={line.tool === "eraser" ? "#000000" : line.color || "#1d4ed8"}
                        strokeWidth={line.width || (line.tool === "eraser" ? 28 : 2.5)}
                        tension={0}
                        lineCap="round"
                        lineJoin="round"
                        globalCompositeOperation={line.tool === "eraser" ? "destination-out" : "source-over"}
                        listening={false}
                        perfectDrawEnabled={false}
                      />
                    );
                  })}
                </Layer>
              </Stage>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
